import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
  useContext
} from "react";
import { createSelector } from "reselect";
import {
  invert,
  toNumber,
  isEmpty,
  min,
  max,
  camelCase,
  startCase,
  noop,
  cloneDeep,
  keyBy,
  omit,
  forEach,
  lowerCase,
  get,
  omitBy,
  times,
  toArray,
  isFunction,
  isEqual,
  every,
  some,
  identity
} from "lodash-es";
import {
  Button,
  Menu,
  MenuItem,
  ContextMenu,
  Icon,
  Intent,
  Callout,
  Tooltip,
  useHotkeys
} from "@blueprintjs/core";
import { arrayMove } from "@dnd-kit/sortable";
import classNames from "classnames";
import scrollIntoView from "dom-scroll-into-view";
import ReactTable, { VIRTUALIZE_CUTOFF_LENGTH } from "@teselagen/react-table";
import immer, { produceWithPatches, enablePatches, applyPatches } from "immer";
import papaparse from "papaparse";
import { useDispatch, useSelector } from "react-redux";
import { ThComponent } from "./ThComponent";
import {
  defaultParsePaste,
  formatPasteData,
  getAllRows,
  getCellCopyText,
  getCellInfo,
  getEntityIdToEntity,
  getFieldPathToIndex,
  getIdOrCodeOrIndex,
  getLastSelectedEntity,
  getNewEntToSelect,
  getRecordsFromIdMap,
  getRowCopyText,
  handleCopyColumn,
  handleCopyHelper,
  handleCopyRows,
  handleCopyTable,
  isEntityClean,
  PRIMARY_SELECTED_VAL,
  removeCleanRows
} from "./utils";
import rowClick, {
  changeSelectedEntities,
  finalizeSelection
} from "./utils/rowClick";
import PagingTool from "./PagingTool";
import SearchBar from "./SearchBar";
import DisplayOptions from "./DisplayOptions";
import DisabledLoadingComponent from "./DisabledLoadingComponent";
import SortableColumns from "./SortableColumns";
import dataTableEnhancer from "./dataTableEnhancer";
import "../toastr";
import "@teselagen/react-table/react-table.css";
import "./style.css";
import shortid from "shortid";
import { SwitchField } from "../FormComponents";
import { validateTableWideErrors } from "./validateTableWideErrors";
import { editCellHelper } from "./editCellHelper";
import getTableConfigFromStorage from "./utils/getTableConfigFromStorage";
import { viewColumn, openColumn, multiViewColumn } from "./viewColumn";
import convertSchema from "./utils/convertSchema";
import TableFormTrackerContext from "./TableFormTrackerContext";
import {
  getQueryParams,
  makeDataTableHandlers,
  getCurrentParamsFromUrl,
  setCurrentParamsOnUrl
} from "./utils/queryParams";
import { useColumns } from "./Columns";
import { formValueSelector, change as _change } from "redux-form";
import { throwFormError } from "../throwFormError";
import { isObservableArray, toJS } from "mobx";
import { isBeingCalledExcessively } from "../utils/isBeingCalledExcessively";
import { getCCDisplayName } from "./utils/tableQueryParamsToHasuraClauses";
import {
  useDeepEqualMemo,
  useDeepEqualMemoIgnoreFns
} from "../utils/hooks/useDeepEqualMemoIgnoreFns";
enablePatches();
const IS_LINUX = window.navigator.platform.toLowerCase().search("linux") > -1;
const itemSizeEstimators = {
  compact: () => 25.34,
  normal: () => 33.34,
  comfortable: () => 41.34
};
const DataTable = ({
  controlled_pageSize,
  formName = "tgDataTable",
  history,
  doNotSearchHiddenColumns,
  isSimple,
  isLocalCall = true,
  isTableParamsConnected,
  noForm,
  orderByFirstColumn,
  schema: _schema,
  showEmptyColumnsByDefault,
  tableParams: _tableParams,
  anyTouched,
  blur,
  ...ownProps
}) => {
  if (isTableParamsConnected && _tableParams && !_tableParams.entities) {
    throw new Error(
      `No entities array detected in tableParams object (<DataTable {...tableParams}/>). You need to call withQuery() after withTableParams() like: compose(withTableParams(), withQuery(something)).`
    );
  }
  const dispatch = useDispatch();
  const change = useCallback(
    (...args) => dispatch(_change(formName, ...args)),
    [dispatch, formName]
  );
  const tableRef = useRef();
  const alreadySelected = useRef(false);
  const [noVirtual, setNoVirtual] = useState(false);
  const [onlyShowRowsWErrors, setOnlyShowRowsWErrors] = useState(false);
  const [entitiesUndoRedoStack, setEntitiesUndoRedoStack] = useState({
    currentVersion: 0
  });
  const [selectedCells, setSelectedCells] = useState({});
  const _tableConfig = getTableConfigFromStorage(formName);
  const userSetPageSize = _tableConfig?.userSetPageSize && parseInt(_tableConfig.userSetPageSize, 10);
  const formTracker = useContext(TableFormTrackerContext);
  useEffect(() => {
    if (formTracker.isActive && !formTracker.formNames.includes(formName)) {
      formTracker.pushFormName(formName);
    }
  }, [formTracker, formName]);
  const [showForcedHiddenColumns, setShowForcedHidden] = useState(() => {
    if (showEmptyColumnsByDefault) {
      return true;
    }
    return false;
  });
  const dtFormParamsSelector = useMemo(
    () => createSelector(
      (state) => formValueSelector(formName)(
        state,
        "reduxFormCellValidation",
        "reduxFormEntities",
        "reduxFormQueryParams",
        "reduxFormSelectedEntityIdMap"
      ),
      (result) => result
      // identity, but memoized
    ),
    [formName]
  );
  const {
    reduxFormCellValidation: _reduxFormCellValidation,
    reduxFormEditingCell,
    reduxFormEntities,
    reduxFormQueryParams: _reduxFormQueryParams = {},
    reduxFormSelectedEntityIdMap: _reduxFormSelectedEntityIdMap = {}
  } = useSelector(dtFormParamsSelector);
  const reduxFormCellValidation = useDeepEqualMemoIgnoreFns(
    _reduxFormCellValidation
  );
  const reduxFormQueryParams = useDeepEqualMemoIgnoreFns(_reduxFormQueryParams);
  const reduxFormSelectedEntityIdMap = useDeepEqualMemoIgnoreFns(
    _reduxFormSelectedEntityIdMap
  );
  let props = ownProps;
  if (!isTableParamsConnected) {
    const normalizedEntities = isObservableArray(ownProps.entities) ? toJS(ownProps.entities) : ownProps.entities;
    props = {
      ...ownProps,
      ..._tableParams,
      entities: normalizedEntities
    };
  }
  const __schema = useDeepEqualMemo(_schema, true);
  const convertedSchema = useMemo(() => convertSchema(__schema), [__schema]);
  if (isLocalCall) {
    if (!noForm && (!formName || formName === "tgDataTable")) {
      throw new Error(
        "Please pass a unique 'formName' prop to the locally connected <DataTable/> component with schema: ",
        _schema
      );
    }
  } else {
    if (!formName || formName === "tgDataTable") {
      throw new Error(
        "Please pass a unique 'formName' prop to the withTableParams() with schema: ",
        _schema
      );
    }
  }
  const { withPaging = !isSimple } = props;
  const {
    doNotCoercePageSize,
    isInfinite = isSimple && !withPaging,
    urlConnected,
    withSelectedEntities
  } = props;
  const defaults = useMemo(() => {
    const _defaults = {
      pageSize: controlled_pageSize || 25,
      order: [],
      // ["-name", "statusCode"] //an array of camelCase display names with - sign to denote reverse
      searchTerm: "",
      page: 1,
      filters: [
        // filters look like this:
        // {
        //   selectedFilter: 'textContains', //camel case
        //   filterOn: ccDisplayName, //camel case display name
        //   filterValue: 'thomas',
        // }
      ],
      ...props.defaults || {}
    };
    if (isLocalCall && orderByFirstColumn && !_defaults?.order?.length) {
      const r = [getCCDisplayName(convertedSchema.fields[0])];
      _defaults.order = r;
    }
    if (userSetPageSize) {
      _defaults.pageSize = userSetPageSize;
    }
    return _defaults;
  }, [
    controlled_pageSize,
    convertedSchema.fields,
    isLocalCall,
    orderByFirstColumn,
    props.defaults,
    userSetPageSize
  ]);
  const selectedEntities = withSelectedEntities ? getRecordsFromIdMap(reduxFormSelectedEntityIdMap) : void 0;
  props = {
    ...props,
    ...withSelectedEntities && typeof withSelectedEntities === "string" && {
      [withSelectedEntities]: selectedEntities
    }
  };
  const _currentParams = useMemo(() => {
    const tmp = (urlConnected ? getCurrentParamsFromUrl(history.location) : reduxFormQueryParams) || {};
    return tmp;
  }, [history, reduxFormQueryParams, urlConnected]);
  const currentParams = useDeepEqualMemoIgnoreFns(_currentParams);
  const tableParams = useMemo(() => {
    if (!isTableParamsConnected) {
      const setNewParams2 = (newParams) => {
        change("reduxFormQueryParams", (prev) => {
          let tmp = newParams;
          if (typeof tmp === "function") tmp = newParams(prev);
          urlConnected && setCurrentParamsOnUrl(tmp, history?.replace);
          return tmp;
        });
      };
      const dispatchProps = makeDataTableHandlers({
        setNewParams: setNewParams2,
        defaults,
        onlyOneFilter: props.onlyOneFilter
      });
      const changeFormValue = (...args) => change(...args);
      return {
        changeFormValue,
        selectedEntities,
        ..._tableParams,
        ...dispatchProps,
        isTableParamsConnected: true
        //let the table know not to do local sorting/filtering etc.
      };
    }
    return _tableParams;
  }, [
    _tableParams,
    change,
    defaults,
    history?.replace,
    isTableParamsConnected,
    props.onlyOneFilter,
    selectedEntities,
    urlConnected
  ]);
  props = {
    ...props,
    ...tableParams
  };
  const queryParams = useMemo(() => {
    if (!isTableParamsConnected) {
      return getQueryParams({
        doNotCoercePageSize,
        currentParams,
        entities: props.entities,
        // for local table
        urlConnected,
        defaults,
        schema: convertedSchema,
        isInfinite,
        isLocalCall,
        additionalFilter: props.additionalFilter,
        noOrderError: props.noOrderError,
        isCodeModel: props.isCodeModel,
        ownProps: props
      });
    }
    return {};
  }, [
    props.entities,
    props.noOrderError,
    props.isCodeModel,
    convertedSchema,
    currentParams,
    doNotCoercePageSize,
    isInfinite,
    isLocalCall,
    isTableParamsConnected,
    urlConnected
  ]);
  props = {
    ...props,
    ...queryParams
  };
  const {
    addFilters = noop,
    additionalFilters,
    additionalFooterButtons,
    autoFocusSearch,
    cellRenderer,
    children,
    className = "",
    clearFilters = noop,
    compact: _compact = true,
    compactPaging,
    contextMenu = noop,
    controlled_hasNextPage,
    controlled_onRefresh,
    controlled_page,
    controlled_setPage,
    controlled_setPageSize,
    controlled_total,
    disabled = false,
    disableSetPageSize,
    doNotShowEmptyRows,
    doNotValidateUntouchedRows,
    editingCellSelectAll,
    entities: _origEntities = [],
    entitiesAcrossPages: _entitiesAcrossPages,
    entityCount,
    errorParsingUrlString,
    expandAllByDefault,
    extraClasses = "",
    extraCompact: _extraCompact,
    filters = [],
    fragment,
    getCellHoverText,
    getRowClassName,
    helperProp,
    hideColumnHeader,
    hideDisplayOptionsIcon,
    hidePageSizeWhenPossible = isSimple ? !withPaging : false,
    hideSelectedCount = isSimple,
    hideSetPageSize,
    hideTotalPages,
    selectedIds,
    isCellEditable,
    isCopyable = true,
    isEntityDisabled = noop,
    isLoading = false,
    isOpenable,
    isSingleSelect = false,
    isViewable,
    recordIdToIsVisibleMap,
    setRecordIdToIsVisibleMap,
    keepSelectionOnPageChange,
    leftOfSearchBarItems,
    maxHeight = 600,
    minimalStyle,
    mustClickCheckboxToSelect,
    noDeselectAll,
    hideExpandSubCompColumn,
    noFooter = isSimple ? !withPaging : false,
    noFullscreenButton = isSimple,
    noHeader = false,
    noPadding = isSimple,
    noRowsFoundMessage,
    noSelect = false,
    noUserSelect,
    onDeselect = noop,
    onDoubleClick,
    onMultiRowSelect = noop,
    onRefresh,
    onRowClick = noop,
    onRowSelect = noop,
    onSingleRowSelect = noop,
    order,
    page = 1,
    pageSize: _pageSize = 10,
    pagingDisabled,
    removeSingleFilter,
    ReactTableProps = {},
    safeQuery,
    searchMenuButton,
    searchTerm,
    selectAllByDefault,
    setNewParams,
    setOrder,
    setPage = noop,
    setPageSize = noop,
    setSearchTerm = noop,
    shouldShowSubComponent,
    showCount = true,
    style = {},
    SubComponent,
    subHeader,
    tableConfigurations,
    tableName,
    topLeftItems,
    variables,
    withCheckboxes = false,
    withDisplayOptions,
    withExpandAndCollapseAllButton,
    withFilter,
    withSearch = !isSimple,
    withSelectAll,
    withSort,
    withTitle = !isSimple,
    noExcessiveCheck,
    isEntityCountLoading,
    getCheckboxGroupId
  } = props;
  const _entities = useMemo(
    () => (reduxFormEntities?.length ? reduxFormEntities : _origEntities) || [],
    [_origEntities, reduxFormEntities]
  );
  const entities = useDeepEqualMemoIgnoreFns(_entities);
  const entitiesAcrossPages = useDeepEqualMemoIgnoreFns(_entitiesAcrossPages);
  useEffect(() => {
    !noExcessiveCheck && isBeingCalledExcessively({ uniqName: `dt_entities_${formName}` });
    change("allOrderedEntities", entitiesAcrossPages);
    if (entities.length === 0 || isEmpty(reduxFormSelectedEntityIdMap)) return;
    changeSelectedEntities({
      idMap: reduxFormSelectedEntityIdMap,
      entities,
      change
    });
  }, [
    entitiesAcrossPages,
    reduxFormSelectedEntityIdMap,
    change,
    noExcessiveCheck,
    entities,
    formName
  ]);
  const [tableConfig, _setTableConfig] = useState({ fieldOptions: [] });
  const setTableConfig = useCallback(
    (newConfig) => {
      _setTableConfig((prev) => {
        let newConfigVal = newConfig;
        if (typeof newConfig === "function") {
          newConfigVal = newConfig(prev);
        }
        if (!isEqual(prev.fieldOptions, newConfigVal.fieldOptions)) {
          change(
            "reduxFormReadOnlyFieldOptions",
            newConfigVal.fieldOptions || []
          );
        }
        return newConfigVal;
      });
    },
    [change]
  );
  useEffect(() => {
    if (withDisplayOptions) {
      let newTableConfig = {};
      newTableConfig = getTableConfigFromStorage(formName);
      !noExcessiveCheck && isBeingCalledExcessively({ uniqName: `dt_setTableConfig_${formName}` });
      setTableConfig((prev) => {
        if (!newTableConfig) {
          newTableConfig = {
            fieldOptions: []
          };
        }
        if (isEqual(prev, newTableConfig)) {
          return prev;
        } else {
          return newTableConfig;
        }
      });
    }
  }, [
    convertedSchema,
    // If the schema changes we want to take into account the synced tableConfig again
    formName,
    setTableConfig,
    tableConfigurations,
    withDisplayOptions,
    noExcessiveCheck
  ]);
  const schema = useMemo(() => {
    const schema2 = { ...convertedSchema };
    if (isViewable) {
      schema2.fields = [viewColumn, ...schema2.fields];
    }
    if (recordIdToIsVisibleMap) {
      schema2.fields = [multiViewColumn, ...schema2.fields];
    }
    if (isOpenable) {
      schema2.fields = [
        openColumn({ onDoubleClick, history }),
        ...schema2.fields
      ];
    }
    schema2.fields = schema2.fields.map((field) => {
      if (field.placementPath) {
        return {
          ...field,
          sortDisabled: field.sortDisabled || typeof field.path === "string" && field.path.includes("."),
          path: field.placementPath
        };
      } else {
        return field;
      }
    });
    if (withDisplayOptions) {
      const fieldOptsByPath = keyBy(tableConfig.fieldOptions, "path");
      schema2.fields = schema2.fields.map((field) => {
        const fieldOpt = fieldOptsByPath[field.path];
        let noValsForField = false;
        if (!showForcedHiddenColumns && withDisplayOptions && (isSimple || isInfinite)) {
          noValsForField = entities.every((e) => {
            const val = get(e, field.path);
            return field.render ? !field.render(val, e, void 0, {
              currentParams,
              setNewParams
            }) : cellRenderer?.[field.path] ? !cellRenderer[field.path](val, e, void 0, {
              currentParams,
              setNewParams
            }) : !val;
          });
        }
        if (noValsForField && entities.length) {
          return {
            ...field,
            isHidden: true
          };
        } else if (fieldOpt) {
          return {
            ...field,
            isHidden: fieldOpt.isHidden
          };
        } else {
          return field;
        }
      });
      const columnOrderings = tableConfig.columnOrderings;
      if (columnOrderings) {
        const fieldsWithOrders = [];
        const fieldsWithoutOrder = [];
        schema2.fields.forEach((field) => {
          if (columnOrderings.indexOf(field.path) > -1) {
            fieldsWithOrders.push(field);
          } else {
            fieldsWithoutOrder.push(field);
          }
        });
        schema2.fields = fieldsWithOrders.sort(({ path: path1 }, { path: path2 }) => {
          return columnOrderings.indexOf(path1) - columnOrderings.indexOf(path2);
        }).concat(fieldsWithoutOrder);
        if (!isEqual(
          schema2.fields.map((f) => f.path),
          columnOrderings
        )) {
          setTableConfig((prev) => ({
            ...prev,
            columnOrderings: schema2.fields.map((f) => f.path)
          }));
        }
      }
    }
    return schema2;
  }, [
    cellRenderer,
    convertedSchema,
    currentParams,
    entities,
    setTableConfig,
    history,
    isInfinite,
    isOpenable,
    isSimple,
    isViewable,
    onDoubleClick,
    setNewParams,
    showForcedHiddenColumns,
    tableConfig.columnOrderings,
    tableConfig.fieldOptions,
    withDisplayOptions,
    recordIdToIsVisibleMap
  ]);
  const [columns, setColumns] = useState([]);
  const {
    moveColumnPersist,
    persistPageSize,
    resetDefaultVisibility,
    resizePersist,
    updateColumnVisibility,
    updateTableDisplayDensity
  } = useMemo(() => {
    let resetDefaultVisibility2;
    let updateColumnVisibility2;
    let updateTableDisplayDensity2;
    let persistPageSize2;
    let moveColumnPersist2;
    let resizePersist2 = noop;
    if (withDisplayOptions) {
      const fieldOptsByPath = keyBy(tableConfig.fieldOptions, "path");
      const syncStorage = (newTableConfig) => {
        setTableConfig(newTableConfig);
        window.localStorage.setItem(formName, JSON.stringify(newTableConfig));
      };
      resetDefaultVisibility2 = function() {
        setTableConfig({ fieldOptions: [] });
        window.localStorage.removeItem(formName);
      };
      updateColumnVisibility2 = function({ path, paths, shouldShow }) {
        const newFieldOpts = {
          ...fieldOptsByPath
        };
        const pathsToUse = paths ? paths : [path];
        pathsToUse.forEach((path2) => {
          newFieldOpts[path2] = { path: path2, isHidden: !shouldShow };
        });
        syncStorage({ ...tableConfig, fieldOptions: toArray(newFieldOpts) });
      };
      updateTableDisplayDensity2 = function(density) {
        syncStorage({ ...tableConfig, density });
      };
      persistPageSize2 = function(pageSize2) {
        syncStorage({ ...tableConfig, userSetPageSize: pageSize2 });
      };
      moveColumnPersist2 = function({ oldIndex, newIndex }) {
        const columnOrderings = tableConfig.columnOrderings || columns.map(({ path }) => path);
        syncStorage({
          ...tableConfig,
          columnOrderings: arrayMove(columnOrderings, oldIndex, newIndex)
        });
      };
      resizePersist2 = function(newResized) {
        syncStorage({ ...tableConfig, resized: newResized });
      };
    }
    return {
      moveColumnPersist: moveColumnPersist2,
      persistPageSize: persistPageSize2,
      resetDefaultVisibility: resetDefaultVisibility2,
      resizePersist: resizePersist2,
      updateColumnVisibility: updateColumnVisibility2,
      updateTableDisplayDensity: updateTableDisplayDensity2
    };
  }, [formName, setTableConfig, columns, tableConfig, withDisplayOptions]);
  let compact = _compact;
  let extraCompact = _extraCompact;
  if (withDisplayOptions && tableConfig.density) {
    compact = tableConfig.density === "compact";
    extraCompact = tableConfig.density === "extraCompact";
  }
  const resized = useMemo(
    () => tableConfig.resized || [],
    [tableConfig?.resized]
  );
  const pageSize = controlled_pageSize || _pageSize;
  const [expandedEntityIdMap, setExpandedEntityIdMap] = useState(() => {
    const initialExpandedEntityIdMap = {};
    if (expandAllByDefault) {
      entities.forEach((entity) => {
        initialExpandedEntityIdMap[entity.id || entity.code] = true;
      });
    }
    return initialExpandedEntityIdMap;
  });
  const updateValidation = useCallback(
    (entities2, newCellValidate) => {
      const tableWideErr = validateTableWideErrors({
        entities: entities2,
        schema,
        newCellValidate
      });
      change("reduxFormCellValidation", tableWideErr);
    },
    [schema, change]
  );
  const updateEntitiesHelper = useCallback(
    (ents, fn) => {
      const [nextState, patches, inversePatches] = produceWithPatches(ents, fn);
      if (!inversePatches.length) return;
      const thatNewNew = [...nextState];
      thatNewNew.isDirty = true;
      change("reduxFormEntities", thatNewNew);
      setEntitiesUndoRedoStack((prev) => ({
        ...omitBy(prev, (v, k) => {
          return toNumber(k) > prev.currentVersion + 1;
        }),
        currentVersion: prev.currentVersion + 1,
        [prev.currentVersion + 1]: {
          inversePatches,
          patches
        }
      }));
    },
    [change]
  );
  const formatAndValidateEntities = useCallback(
    (entities2, { useDefaultValues, indexToStartAt } = {}) => {
      const editableFields = schema.fields.filter((f) => !f.isNotEditable);
      const validationErrors = {};
      const newEnts = immer(entities2, (entities3) => {
        entities3.forEach((e, index) => {
          editableFields.forEach((columnSchema) => {
            if (useDefaultValues) {
              if (e[columnSchema.path] === void 0) {
                if (isFunction(columnSchema.defaultValue)) {
                  e[columnSchema.path] = columnSchema.defaultValue(
                    index + indexToStartAt,
                    e
                  );
                } else e[columnSchema.path] = columnSchema.defaultValue;
              }
            }
            const { error } = editCellHelper({
              entity: e,
              columnSchema,
              newVal: e[columnSchema.path]
            });
            if (error) {
              const rowId = getIdOrCodeOrIndex(e, index);
              validationErrors[`${rowId}:${columnSchema.path}`] = error;
            }
          });
        });
      });
      return {
        newEnts,
        validationErrors
      };
    },
    [schema.fields]
  );
  const updateValidationHelper = useCallback(() => {
    updateValidation(entities, reduxFormCellValidation);
  }, [entities, reduxFormCellValidation, updateValidation]);
  const addEditableTableEntities = useCallback(
    (incomingEnts) => {
      updateEntitiesHelper(entities, (entities2) => {
        const newEntities = incomingEnts.map((e) => ({
          ...e,
          id: e.id || shortid(),
          _isClean: false
        }));
        const { newEnts, validationErrors } = formatAndValidateEntities(
          newEntities,
          {
            useDefaultValues: true,
            indexToStartAt: entities2.length
          }
        );
        if (every(entities2, "_isClean")) {
          forEach(newEnts, (e, i) => {
            entities2[i] = e;
          });
        } else {
          entities2.splice(entities2.length, 0, ...newEnts);
        }
        updateValidation(entities2, {
          ...reduxFormCellValidation,
          ...validationErrors
        });
      });
    },
    [
      entities,
      formatAndValidateEntities,
      reduxFormCellValidation,
      updateEntitiesHelper,
      updateValidation
    ]
  );
  const getEditableTableInfoAndThrowFormError = useCallback(() => {
    const { entsToUse, validationToUse } = removeCleanRows(
      reduxFormEntities,
      reduxFormCellValidation
    );
    const validationWTableErrs = validateTableWideErrors({
      entities: entsToUse,
      schema,
      newCellValidate: validationToUse
    });
    if (!entsToUse?.length) {
      throwFormError(
        "Please add at least one row to the table before submitting."
      );
    }
    const invalid = isEmpty(validationWTableErrs) || !some(validationWTableErrs, (v) => v) ? void 0 : validationWTableErrs;
    if (invalid) {
      throwFormError("Please fix the errors in the table before submitting.");
    }
    return entsToUse;
  }, [reduxFormCellValidation, reduxFormEntities, schema]);
  useEffect(() => {
    if (helperProp) {
      helperProp.updateValidationHelper = updateValidationHelper;
      helperProp.addEditableTableEntities = addEditableTableEntities;
      helperProp.getEditableTableInfoAndThrowFormError = getEditableTableInfoAndThrowFormError;
    }
  }, [
    addEditableTableEntities,
    getEditableTableInfoAndThrowFormError,
    helperProp,
    updateValidationHelper
  ]);
  const handleRowMove = useCallback(
    (type, shiftHeld) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      let newIdMap = {};
      const lastSelectedEnt = getLastSelectedEntity(
        reduxFormSelectedEntityIdMap
      );
      if (noSelect) return;
      if (lastSelectedEnt) {
        let lastSelectedIndex = entities.findIndex(
          (ent) => ent === lastSelectedEnt
        );
        if (lastSelectedIndex === -1) {
          if (lastSelectedEnt.id !== void 0) {
            lastSelectedIndex = entities.findIndex(
              (ent) => ent.id === lastSelectedEnt.id
            );
          } else if (lastSelectedEnt.code !== void 0) {
            lastSelectedIndex = entities.findIndex(
              (ent) => ent.code === lastSelectedEnt.code
            );
          }
        }
        if (lastSelectedIndex === -1) {
          return;
        }
        const newEntToSelect = getNewEntToSelect({
          type,
          lastSelectedIndex,
          entities,
          isEntityDisabled
        });
        if (!newEntToSelect) return;
        if (shiftHeld && !isSingleSelect) {
          if (reduxFormSelectedEntityIdMap[newEntToSelect.id || newEntToSelect.code]) {
            newIdMap = omit(reduxFormSelectedEntityIdMap, [
              lastSelectedEnt.id || lastSelectedEnt.code
            ]);
            newIdMap[newEntToSelect.id || newEntToSelect.code].time = Date.now() + 1;
          } else {
            newIdMap = {
              ...reduxFormSelectedEntityIdMap,
              [newEntToSelect.id || newEntToSelect.code]: {
                entity: newEntToSelect,
                time: Date.now()
              }
            };
          }
        } else {
          newIdMap[newEntToSelect.id || newEntToSelect.code] = {
            entity: newEntToSelect,
            time: Date.now()
          };
        }
      }
      finalizeSelection({
        idMap: newIdMap,
        entities,
        props: {
          onDeselect,
          onSingleRowSelect,
          onMultiRowSelect,
          noDeselectAll,
          onRowSelect,
          noSelect,
          change
        }
      });
    },
    [
      change,
      entities,
      isEntityDisabled,
      isSingleSelect,
      noDeselectAll,
      noSelect,
      onDeselect,
      onMultiRowSelect,
      onRowSelect,
      onSingleRowSelect,
      reduxFormSelectedEntityIdMap
    ]
  );
  const primarySelectedCellId = useMemo(() => {
    for (const k of Object.keys(selectedCells)) {
      if (selectedCells[k] === PRIMARY_SELECTED_VAL) {
        return k;
      }
    }
  }, [selectedCells]);
  const startCellEdit = useCallback(
    (cellId, shouldClear) => {
      if (shouldClear) {
        change("shouldEditableCellInputBeCleared", true);
      } else {
        change("shouldEditableCellInputBeCleared", false);
      }
      change("reduxFormEditingCell", (prev) => {
        if (prev === cellId) return cellId;
        setSelectedCells((prev2) => {
          if (prev2[cellId] === PRIMARY_SELECTED_VAL) {
            return prev2;
          }
          return { ...prev2, [cellId]: PRIMARY_SELECTED_VAL };
        });
        return cellId;
      });
    },
    [change]
  );
  const handleEnterStartCellEdit = useCallback(
    (e) => {
      e.stopPropagation();
      startCellEdit(primarySelectedCellId);
    },
    [primarySelectedCellId, startCellEdit]
  );
  const handleDeleteCell = useCallback(() => {
    const newCellValidate = {
      ...reduxFormCellValidation
    };
    if (isEmpty(selectedCells)) return;
    const rowIds = [];
    updateEntitiesHelper(entities, (entities2) => {
      const entityIdToEntity = getEntityIdToEntity(entities2);
      Object.keys(selectedCells).forEach((cellId) => {
        const [rowId, path] = cellId.split(":");
        rowIds.push(rowId);
        const entity = entityIdToEntity[rowId].e;
        delete entity._isClean;
        const { error } = editCellHelper({
          entity,
          path,
          schema,
          newVal: ""
        });
        if (error) {
          newCellValidate[cellId] = error;
        } else {
          delete newCellValidate[cellId];
        }
      });
      updateValidation(entities2, newCellValidate);
    });
  }, [
    entities,
    reduxFormCellValidation,
    schema,
    selectedCells,
    updateEntitiesHelper,
    updateValidation
  ]);
  const waitUntilAllRowsAreRendered = useCallback(() => {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        const allRowEls = tableRef.current?.tableRef?.querySelectorAll(".rt-tr-group");
        if (allRowEls?.length === entities.length) {
          clearInterval(interval);
          resolve();
        }
      }, 50);
    });
  }, []);
  const handleCopySelectedCells = useCallback(async () => {
    if (isEmpty(selectedCells)) return;
    if (entities.length > VIRTUALIZE_CUTOFF_LENGTH) {
      setNoVirtual(true);
      await waitUntilAllRowsAreRendered();
    }
    const pathToIndex = getFieldPathToIndex(schema);
    const entityIdToEntity = getEntityIdToEntity(entities);
    const selectionGrid = [];
    let firstRowIndex;
    let firstCellIndex;
    Object.keys(selectedCells).forEach((key) => {
      const [rowId, path] = key.split(":");
      const eInfo = entityIdToEntity[rowId];
      if (eInfo) {
        if (firstRowIndex === void 0 || eInfo.i < firstRowIndex) {
          firstRowIndex = eInfo.i;
        }
        if (!selectionGrid[eInfo.i]) {
          selectionGrid[eInfo.i] = [];
        }
        const cellIndex = pathToIndex[path];
        if (firstCellIndex === void 0 || cellIndex < firstCellIndex) {
          firstCellIndex = cellIndex;
        }
        selectionGrid[eInfo.i][cellIndex] = true;
      }
    });
    if (firstRowIndex === void 0) return;
    const allRows = getAllRows(tableRef);
    let fullCellText = "";
    const fullJson = [];
    times(selectionGrid.length, (i) => {
      const row = selectionGrid[i];
      if (fullCellText) {
        fullCellText += "\n";
      }
      if (!row) {
        return;
      } else {
        const jsonRow = [];
        let [rowCopyText, json] = getRowCopyText(allRows[i + 1]);
        rowCopyText = rowCopyText.split("	");
        times(row.length, (i2) => {
          const cell = row[i2];
          if (cell) {
            fullCellText += rowCopyText[i2];
            jsonRow.push(json[i2]);
          }
          if (i2 !== row.length - 1 && i2 >= firstCellIndex) fullCellText += "	";
        });
        fullJson.push(jsonRow);
      }
    });
    if (!fullCellText) return window.toastr.warning("No text to copy");
    handleCopyHelper(fullCellText, fullJson, "Selected cells copied");
    setNoVirtual(false);
  }, [entities, selectedCells, schema, waitUntilAllRowsAreRendered]);
  const handleCopySelectedRows = useCallback(
    async (selectedRecords) => {
      if (entities.length > VIRTUALIZE_CUTOFF_LENGTH) {
        setNoVirtual(true);
        await waitUntilAllRowsAreRendered();
      }
      const idToIndex = entities.reduce((acc, e, i) => {
        acc[e.id || e.code] = i;
        return acc;
      }, {});
      const rowNumbersToCopy = [];
      selectedRecords.forEach((rec) => {
        const rowIndex = idToIndex[rec.id || rec.code] + 1;
        if (!rowNumbersToCopy.includes(rowIndex)) {
          rowNumbersToCopy.push(rowIndex);
        }
      });
      rowNumbersToCopy.sort();
      if (!rowNumbersToCopy.length) return;
      rowNumbersToCopy.unshift(0);
      try {
        const allRowEls = getAllRows(tableRef);
        if (!allRowEls) return;
        const rowEls = rowNumbersToCopy.map((i) => allRowEls[i]).filter(identity);
        if (window.Cypress) window.Cypress.__copiedRowsLength = rowEls.length;
        handleCopyRows(rowEls, {
          onFinishMsg: "Selected rows copied"
        });
      } catch (error) {
        console.error(`error:`, error);
        window.toastr.error("Error copying rows.");
      }
      setNoVirtual(false);
    },
    [entities, waitUntilAllRowsAreRendered]
  );
  const handleCopyHotkey = useCallback(
    (e) => {
      if (isCellEditable) {
        handleCopySelectedCells(e);
      } else {
        handleCopySelectedRows(
          getRecordsFromIdMap(reduxFormSelectedEntityIdMap),
          e
        );
      }
    },
    [
      handleCopySelectedCells,
      handleCopySelectedRows,
      isCellEditable,
      reduxFormSelectedEntityIdMap
    ]
  );
  const handleCut = useCallback(
    (e) => {
      handleDeleteCell();
      handleCopyHotkey(e);
    },
    [handleCopyHotkey, handleDeleteCell]
  );
  const flashTableBorder = () => {
    try {
      const table = tableRef.current.tableRef;
      table.classList.add("tgBorderBlue");
      setTimeout(() => {
        table.classList.remove("tgBorderBlue");
      }, 300);
    } catch (e) {
      console.error(`err when flashing table border:`, e);
    }
  };
  const handleUndo = useCallback(() => {
    if (entitiesUndoRedoStack.currentVersion > 0) {
      flashTableBorder();
      const nextState = applyPatches(
        entities,
        entitiesUndoRedoStack[entitiesUndoRedoStack.currentVersion].inversePatches
      );
      const { newEnts, validationErrors } = formatAndValidateEntities(nextState);
      setEntitiesUndoRedoStack((prev) => ({
        ...prev,
        currentVersion: prev.currentVersion - 1
      }));
      updateValidation(newEnts, validationErrors);
      change("reduxFormEntities", newEnts);
    }
  }, [
    change,
    entities,
    formatAndValidateEntities,
    entitiesUndoRedoStack,
    updateValidation
  ]);
  const handleRedo = useCallback(() => {
    const nextV = entitiesUndoRedoStack.currentVersion + 1;
    if (entitiesUndoRedoStack[nextV]) {
      flashTableBorder();
      const nextState = applyPatches(
        entities,
        entitiesUndoRedoStack[nextV].patches
      );
      const { newEnts, validationErrors } = formatAndValidateEntities(nextState);
      change("reduxFormEntities", newEnts);
      updateValidation(newEnts, validationErrors);
      setEntitiesUndoRedoStack((prev) => ({
        ...prev,
        currentVersion: nextV
      }));
    }
  }, [
    change,
    entities,
    formatAndValidateEntities,
    entitiesUndoRedoStack,
    updateValidation
  ]);
  const handleSelectAllRows = useCallback(
    (e) => {
      if (isSingleSelect) return;
      e.preventDefault();
      if (isCellEditable) {
        const schemaPaths = schema.fields.map((f) => f.path);
        const newSelectedCells = {};
        entities.forEach((entity, i) => {
          if (isEntityDisabled(entity)) return;
          const entityId = getIdOrCodeOrIndex(entity, i);
          schemaPaths.forEach((p) => {
            newSelectedCells[`${entityId}:${p}`] = true;
          });
        });
        setSelectedCells(newSelectedCells);
      } else {
        const newIdMap = {};
        entities.forEach((entity, i) => {
          if (isEntityDisabled(entity)) return;
          const entityId = getIdOrCodeOrIndex(entity, i);
          newIdMap[entityId] = { entity };
        });
        finalizeSelection({
          idMap: newIdMap,
          entities,
          props: {
            onDeselect,
            onSingleRowSelect,
            onMultiRowSelect,
            noDeselectAll,
            onRowSelect,
            noSelect,
            change
          }
        });
      }
    },
    [
      change,
      entities,
      isCellEditable,
      isEntityDisabled,
      isSingleSelect,
      noDeselectAll,
      noSelect,
      onDeselect,
      onMultiRowSelect,
      onRowSelect,
      onSingleRowSelect,
      schema.fields
    ]
  );
  const hotKeys = useMemo(
    () => [
      {
        global: false,
        combo: "up",
        label: "Move Up a Row",
        onKeyDown: handleRowMove("up")
      },
      {
        global: false,
        combo: "down",
        label: "Move Down a Row",
        onKeyDown: handleRowMove("down")
      },
      {
        global: false,
        combo: "up+shift",
        label: "Move Up a Row",
        onKeyDown: handleRowMove("up", true)
      },
      ...isCellEditable ? [
        {
          global: false,
          combo: "enter",
          label: "Enter -> Start Cell Edit",
          onKeyDown: handleEnterStartCellEdit
        },
        {
          global: false,
          combo: "mod+x",
          label: "Cut",
          onKeyDown: handleCut
        },
        {
          global: false,
          combo: IS_LINUX ? "alt+z" : "mod+z",
          label: "Undo",
          onKeyDown: handleUndo
        },
        {
          global: false,
          combo: IS_LINUX ? "alt+shift+z" : "mod+shift+z",
          label: "Redo",
          onKeyDown: handleRedo
        },
        {
          global: false,
          combo: "backspace",
          label: "Delete Cell",
          onKeyDown: handleDeleteCell
        }
      ] : [],
      {
        global: false,
        combo: "down+shift",
        label: "Move Down a Row",
        onKeyDown: handleRowMove("down", true)
      },
      {
        global: false,
        combo: "mod + c",
        label: "Copy rows",
        onKeyDown: handleCopyHotkey
      },
      {
        global: false,
        combo: "mod + a",
        label: "Select rows",
        onKeyDown: handleSelectAllRows
      }
    ],
    [
      handleCopyHotkey,
      handleCut,
      handleDeleteCell,
      handleEnterStartCellEdit,
      handleRedo,
      handleRowMove,
      handleSelectAllRows,
      handleUndo,
      isCellEditable
    ]
  );
  const { handleKeyDown, handleKeyUp } = useHotkeys(hotKeys);
  const [fullscreen, setFullscreen] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  useEffect(() => {
    const formatAndValidateTableInitial = () => {
      const { newEnts, validationErrors } = formatAndValidateEntities(entities);
      const toKeep = {};
      forEach(reduxFormCellValidation, (v, k) => {
        if (v && v._isTableAsyncWideError) {
          toKeep[k] = v;
        }
      });
      change("reduxFormEntities", (prev) => {
        if (!isEqual(prev, newEnts)) {
          return newEnts;
        }
        return prev;
      });
      updateValidation(newEnts, {
        ...toKeep,
        ...validationErrors
      });
    };
    isCellEditable && formatAndValidateTableInitial();
  }, [
    change,
    entities,
    formatAndValidateEntities,
    isCellEditable,
    reduxFormCellValidation,
    updateValidation
  ]);
  const handlePaste = useCallback(
    (e) => {
      if (isCellEditable) {
        if (isEmpty(selectedCells)) return;
        try {
          let pasteData = [];
          let toPaste;
          if (window.clipboardData && window.clipboardData.getData) {
            toPaste = window.clipboardData.getData("Text");
          } else if (e.clipboardData && e.clipboardData.getData) {
            toPaste = e.clipboardData.getData("text/plain");
          }
          const jsonToPaste = e.clipboardData.getData("application/json");
          try {
            const pastedJson = [];
            JSON.parse(jsonToPaste).forEach((row) => {
              const newRow = [];
              Object.values(row).forEach((cell) => {
                const cellVal = JSON.parse(cell);
                newRow.push(cellVal);
              });
              pastedJson.push(newRow);
            });
            pasteData = pastedJson;
            if (pasteData[0] && pasteData[0][0] && pasteData[0][0].__isHeaderCell) {
              pasteData = pasteData.slice(1);
            }
          } catch (e2) {
            if (toPaste.includes(",")) {
              try {
                const { data, errors } = papaparse.parse(toPaste, {
                  header: false
                });
                if (data?.length && !errors?.length) {
                  pasteData = data;
                }
              } catch (error) {
                console.error(`error p982qhgpf9qh`, error);
              }
            }
          }
          pasteData = pasteData.length ? pasteData : defaultParsePaste(toPaste);
          if (!pasteData || !pasteData.length) return;
          if (pasteData.length === 1 && pasteData[0].length === 1) {
            const newCellValidate = {
              ...reduxFormCellValidation
            };
            const newVal = pasteData[0][0];
            updateEntitiesHelper(entities, (entities2) => {
              const entityIdToEntity = getEntityIdToEntity(entities2);
              Object.keys(selectedCells).forEach((cellId) => {
                const [rowId, path] = cellId.split(":");
                const entity = entityIdToEntity[rowId].e;
                delete entity._isClean;
                const { error } = editCellHelper({
                  entity,
                  path,
                  schema,
                  newVal: formatPasteData({ newVal, path, schema })
                });
                if (error) {
                  newCellValidate[cellId] = error;
                } else {
                  delete newCellValidate[cellId];
                }
              });
              updateValidation(entities2, newCellValidate);
            });
          } else {
            if (primarySelectedCellId) {
              const newCellValidate = {
                ...reduxFormCellValidation
              };
              const newSelectedCells = { ...selectedCells };
              updateEntitiesHelper(entities, (entities2) => {
                const entityIdToEntity = getEntityIdToEntity(entities2);
                const [rowId, primaryCellPath] = primarySelectedCellId.split(":");
                const primaryEntityInfo = entityIdToEntity[rowId];
                const startIndex = primaryEntityInfo.i;
                const endIndex = primaryEntityInfo.i + pasteData.length;
                for (let i = startIndex; i < endIndex; i++) {
                  if (!entities2[i]) {
                    entities2[i] = { id: shortid() };
                  }
                }
                const entitiesToManipulate = entities2.slice(
                  startIndex,
                  endIndex
                );
                const pathToIndex = getFieldPathToIndex(schema);
                const indexToPath = invert(pathToIndex);
                const startCellIndex = pathToIndex[primaryCellPath];
                pasteData.forEach((row, i) => {
                  row.forEach((newVal, j) => {
                    if (newVal) {
                      const cellIndexToChange = startCellIndex + j;
                      const entity = entitiesToManipulate[i];
                      if (entity) {
                        delete entity._isClean;
                        const path = indexToPath[cellIndexToChange];
                        if (path) {
                          const { error } = editCellHelper({
                            entity,
                            path,
                            schema,
                            newVal: formatPasteData({
                              newVal,
                              path,
                              schema
                            })
                          });
                          const cellId = `${getIdOrCodeOrIndex(entity)}:${path}`;
                          if (!newSelectedCells[cellId]) {
                            newSelectedCells[cellId] = true;
                          }
                          if (error) {
                            newCellValidate[cellId] = error;
                          } else {
                            delete newCellValidate[cellId];
                          }
                        }
                      }
                    }
                  });
                });
                updateValidation(entities2, newCellValidate);
              });
              setSelectedCells(newSelectedCells);
            }
          }
        } catch (error) {
          console.error(`error:`, error);
        }
      }
    },
    [
      entities,
      isCellEditable,
      primarySelectedCellId,
      reduxFormCellValidation,
      schema,
      selectedCells,
      updateEntitiesHelper,
      updateValidation
    ]
  );
  useEffect(() => {
    document.addEventListener("paste", handlePaste);
    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [handlePaste]);
  useEffect(() => {
    addFilters(additionalFilters);
  }, [additionalFilters]);
  useEffect(() => {
    const newCols = schema.fields ? schema.fields.reduce((columns2, field, i) => {
      if (field.isHidden) {
        return columns2.concat(field);
      }
      return columns2.concat({
        ...field,
        columnIndex: i
      });
    }, []) : [];
    setColumns(newCols);
  }, [schema?.fields]);
  const setSelectedIds = useCallback(
    (selectedIds2, scrollToFirst) => {
      const idArray = Array.isArray(selectedIds2) ? selectedIds2 : [selectedIds2];
      const selectedEntities2 = entities.filter(
        (e) => idArray.indexOf(getIdOrCodeOrIndex(e)) > -1 && !isEntityDisabled(e)
      );
      const newIdMap = selectedEntities2.reduce((acc, entity) => {
        acc[getIdOrCodeOrIndex(entity)] = { entity };
        return acc;
      }, {});
      setExpandedEntityIdMap(newIdMap);
      finalizeSelection({
        idMap: newIdMap,
        entities,
        props: {
          onDeselect,
          onSingleRowSelect,
          onMultiRowSelect,
          noDeselectAll,
          onRowSelect,
          noSelect,
          change
        }
      });
      if (scrollToFirst) {
        const idToScrollTo = idArray[0];
        if (!idToScrollTo && idToScrollTo !== 0) return;
        const entityIndexToScrollTo = entities.findIndex(
          (e) => e.id === idToScrollTo || e.code === idToScrollTo
        );
        if (entityIndexToScrollTo === -1 || !tableRef.current) return;
        const tableBody = tableRef.current.tableRef.querySelector(".rt-tbody");
        if (!tableBody) return;
        const rowEl = tableBody.getElementsByClassName("rt-tr-group")[entityIndexToScrollTo];
        if (!rowEl) return;
        setTimeout(() => {
          rowEl && tableBody && scrollIntoView(rowEl, tableBody, {
            alignWithTop: true
          });
        }, 0);
      }
    },
    [
      change,
      entities,
      isEntityDisabled,
      noDeselectAll,
      noSelect,
      onDeselect,
      onMultiRowSelect,
      onRowSelect,
      onSingleRowSelect
    ]
  );
  useEffect(() => {
    if (selectedIds) {
      setSelectedIds(selectedIds);
    }
    if (selectAllByDefault && entities && entities.length) {
      if (alreadySelected.current) return;
      setSelectedIds(entities.map(getIdOrCodeOrIndex));
      alreadySelected.current = true;
    }
  }, [entities, selectedIds, selectAllByDefault, setSelectedIds]);
  const TheadComponent = useCallback(
    ({ className: className2, style: style2, children: children2 }) => {
      const moveColumn = ({ oldIndex, newIndex }) => {
        const newColumns = arrayMove(columns, oldIndex, newIndex).map(
          (column, i) => {
            return {
              ...column,
              columnIndex: i
            };
          }
        );
        setColumns(newColumns);
      };
      return /* @__PURE__ */ jsx(
        SortableColumns,
        {
          sortedItemsFull: columns.map((c) => c.path),
          className: className2,
          style: style2,
          moveColumn: moveColumnPersist || moveColumn,
          children: children2
        }
      );
    },
    [columns, moveColumnPersist]
  );
  const addEntitiesToSelection = (entities2) => {
    const idMap2 = reduxFormSelectedEntityIdMap || {};
    const newIdMap = cloneDeep(idMap2) || {};
    entities2.forEach((entity, i) => {
      if (isEntityDisabled(entity)) return;
      const entityId = getIdOrCodeOrIndex(entity, i);
      newIdMap[entityId] = { entity };
    });
    finalizeSelection({
      idMap: newIdMap,
      entities: entities2,
      props: {
        onDeselect,
        onSingleRowSelect,
        onMultiRowSelect,
        noDeselectAll,
        onRowSelect,
        noSelect,
        change
      }
    });
  };
  const refocusTable = useCallback(() => {
    const table = tableRef.current?.tableRef?.closest(
      ".data-table-container>div"
    );
    table?.focus();
  }, []);
  const isSelectionARectangle = useCallback(() => {
    if (selectedCells && Object.keys(selectedCells).length > 1) {
      const pathToIndex = getFieldPathToIndex(schema);
      const entityMap = getEntityIdToEntity(entities);
      let selectionGrid = [];
      let firstCellIndex;
      let lastCellIndex;
      let lastRowIndex;
      let firstRowIndex;
      const selectedPaths = [];
      Object.keys(selectedCells).forEach((key) => {
        const [rowId, cellPath] = key.split(":");
        if (!selectedPaths.includes(cellPath)) selectedPaths.push(cellPath);
        const cellIndex = pathToIndex[cellPath];
        const ent = entityMap[rowId];
        if (!ent) return;
        const { i } = ent;
        if (firstRowIndex === void 0 || i < firstRowIndex) {
          firstRowIndex = i;
        }
        if (lastRowIndex === void 0 || i > lastRowIndex) {
          lastRowIndex = i;
        }
        if (!selectionGrid[i]) selectionGrid[i] = [];
        selectionGrid[i][cellIndex] = { cellId: key, rowIndex: i, cellIndex };
        if (firstCellIndex === void 0 || cellIndex < firstCellIndex) {
          firstCellIndex = cellIndex;
        }
        if (lastCellIndex === void 0 || cellIndex > lastCellIndex) {
          lastCellIndex = cellIndex;
        }
      });
      selectionGrid = selectionGrid.slice(firstRowIndex);
      let isRectangle = true;
      for (let i = 0; i < selectionGrid.length; i++) {
        const row = selectionGrid[i];
        if (!row) {
          isRectangle = false;
          break;
        } else {
          for (let j = firstCellIndex; j < row.length; j++) {
            if (!row[j]) {
              isRectangle = false;
              break;
            }
          }
        }
      }
      if (isRectangle) {
        return {
          entityMap,
          firstCellIndex,
          firstRowIndex,
          isRect: true,
          lastCellIndex,
          lastRowIndex,
          selectedPaths,
          selectionGrid,
          pathToIndex
        };
      } else {
        return {};
      }
    }
    return {};
  }, [entities, schema, selectedCells]);
  const handleCellClick = useCallback(
    ({ event, cellId }) => {
      if (!cellId) return;
      const [rowId, cellPath] = cellId.split(":");
      const entityMap = getEntityIdToEntity(entities);
      const { e: entity, i: rowIndex } = entityMap[rowId];
      const pathToIndex = getFieldPathToIndex(schema);
      const columnIndex = pathToIndex[cellPath];
      const rowDisabled = isEntityDisabled(entity);
      if (rowDisabled) return;
      let newSelectedCells = {
        ...selectedCells
      };
      if (newSelectedCells[cellId] && !event.shiftKey) {
        if (reduxFormEditingCell === cellId) return;
        if (event.metaKey) {
          delete newSelectedCells[cellId];
        } else {
          newSelectedCells = {};
          newSelectedCells[cellId] = PRIMARY_SELECTED_VAL;
        }
      } else {
        if (event.metaKey) {
          if (isEmpty(newSelectedCells)) {
            newSelectedCells[cellId] = PRIMARY_SELECTED_VAL;
          } else {
            newSelectedCells[cellId] = PRIMARY_SELECTED_VAL;
            if (primarySelectedCellId)
              newSelectedCells[primarySelectedCellId] = true;
          }
        } else if (event.shiftKey) {
          if (primarySelectedCellId) {
            const [rowId2, colPath] = primarySelectedCellId.split(":");
            const primaryRowIndex = entities.findIndex((e, i) => {
              return getIdOrCodeOrIndex(e, i) === rowId2;
            });
            const fieldToIndex = getFieldPathToIndex(schema);
            const primaryColIndex = fieldToIndex[colPath];
            if (primaryRowIndex === -1 || primaryColIndex === -1) {
              newSelectedCells = {};
              newSelectedCells[cellId] = PRIMARY_SELECTED_VAL;
            } else {
              const minRowIndex = min([primaryRowIndex, rowIndex]);
              const minColIndex = min([primaryColIndex, columnIndex]);
              const maxRowIndex = max([primaryRowIndex, rowIndex]);
              const maxColIndex = max([primaryColIndex, columnIndex]);
              const entitiesBetweenRows = entities.slice(
                minRowIndex,
                maxRowIndex + 1
              );
              const fieldsBetweenCols = schema.fields.slice(
                minColIndex,
                maxColIndex + 1
              );
              newSelectedCells = {
                [primarySelectedCellId]: PRIMARY_SELECTED_VAL
              };
              entitiesBetweenRows.forEach((e) => {
                const rowId3 = getIdOrCodeOrIndex(e, entities.indexOf(e));
                fieldsBetweenCols.forEach((f) => {
                  const cellId2 = `${rowId3}:${f.path}`;
                  if (!newSelectedCells[cellId2])
                    newSelectedCells[cellId2] = true;
                });
              });
            }
          } else {
            newSelectedCells = {};
            newSelectedCells[cellId] = PRIMARY_SELECTED_VAL;
          }
        } else {
          newSelectedCells = {};
          newSelectedCells[cellId] = PRIMARY_SELECTED_VAL;
        }
      }
      setSelectedCells(newSelectedCells);
    },
    [
      entities,
      isEntityDisabled,
      primarySelectedCellId,
      reduxFormEditingCell,
      schema,
      selectedCells
    ]
  );
  const insertRows = useCallback(
    ({ above, numRows: numRows2 = 1, appendToBottom } = {}) => {
      const [rowId] = primarySelectedCellId?.split(":") || [];
      updateEntitiesHelper(entities, (entities2) => {
        const newEntities = times(numRows2).map(() => ({ id: shortid() }));
        const indexToInsert = entities2.findIndex((e, i) => {
          return getIdOrCodeOrIndex(e, i) === rowId;
        });
        const insertIndex = above ? indexToInsert : indexToInsert + 1;
        const insertIndexToUse = appendToBottom ? entities2.length : insertIndex;
        let { newEnts, validationErrors } = formatAndValidateEntities(
          newEntities,
          {
            useDefaultValues: true,
            indexToStartAt: insertIndexToUse
          }
        );
        newEnts = newEnts.map((e) => ({
          ...e,
          _isClean: true
        }));
        updateValidation(entities2, {
          ...reduxFormCellValidation,
          ...validationErrors
        });
        entities2.splice(insertIndexToUse, 0, ...newEnts);
      });
      refocusTable();
    },
    [
      entities,
      formatAndValidateEntities,
      primarySelectedCellId,
      reduxFormCellValidation,
      refocusTable,
      updateEntitiesHelper,
      updateValidation
    ]
  );
  const showContextMenu = useCallback(
    (e, { idMap: idMap2, selectedCells: selectedCells2 } = {}) => {
      let selectedRecords;
      if (isCellEditable) {
        const rowIds = {};
        Object.keys(selectedCells2).forEach((cellKey) => {
          const [rowId] = cellKey.split(":");
          rowIds[rowId] = true;
        });
        selectedRecords = entities.filter(
          (ent) => rowIds[getIdOrCodeOrIndex(ent)]
        );
      } else {
        selectedRecords = getRecordsFromIdMap(idMap2);
      }
      const itemsToRender = contextMenu({
        selectedRecords,
        history
      });
      if (!itemsToRender && !isCopyable) return null;
      const copyMenuItems = [];
      e.persist();
      if (isCopyable) {
        const cellWrapper = e.target.querySelector(".tg-cell-wrapper") || e.target.closest(".tg-cell-wrapper");
        if (cellWrapper) {
          copyMenuItems.push(
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                onClick: () => {
                  const specificColumn = cellWrapper.getAttribute("data-test");
                  handleCopyRows([cellWrapper.closest(".rt-tr")], {
                    specificColumn,
                    onFinishMsg: "Cell copied"
                  });
                  const [text, jsonText] = getCellCopyText(cellWrapper);
                  handleCopyHelper(text, jsonText);
                },
                text: "Cell"
              },
              "copyCell"
            )
          );
          copyMenuItems.push(
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                onClick: () => {
                  handleCopyColumn(tableRef, cellWrapper);
                },
                text: "Column"
              },
              "copyColumn"
            )
          );
          if (selectedRecords.length > 1) {
            copyMenuItems.push(
              /* @__PURE__ */ jsx(
                MenuItem,
                {
                  onClick: () => {
                    handleCopyColumn(tableRef, cellWrapper, selectedRecords);
                  },
                  text: "Column (Selected)"
                },
                "copyColumnSelected"
              )
            );
          }
        }
        if (selectedRecords.length === 0 || selectedRecords.length === 1) {
          const cell = e.target.querySelector(".tg-cell-wrapper") || e.target.closest(".tg-cell-wrapper") || e.target.closest(".rt-td");
          const row = cell.closest(".rt-tr");
          copyMenuItems.push(
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                onClick: () => {
                  handleCopyRows([row]);
                },
                text: "Row"
              },
              "copySelectedRows"
            )
          );
        } else if (selectedRecords.length > 1) {
          copyMenuItems.push(
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                onClick: () => {
                  handleCopySelectedRows(selectedRecords, e);
                },
                text: "Rows"
              },
              "copySelectedRows"
            )
          );
        }
        copyMenuItems.push(
          /* @__PURE__ */ jsx(
            MenuItem,
            {
              onClick: () => {
                handleCopyTable(tableRef);
              },
              text: "Table"
            },
            "copyFullTableRows"
          )
        );
      }
      const selectedRowIds = Object.keys(selectedCells2).map((cellId) => {
        const [rowId] = cellId.split(":");
        return rowId;
      });
      const menu = /* @__PURE__ */ jsxs(Menu, { children: [
        itemsToRender,
        copyMenuItems.length && /* @__PURE__ */ jsx(MenuItem, { icon: "clipboard", text: "Copy", children: copyMenuItems }, "copyOpts"),
        isCellEditable && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            MenuItem,
            {
              icon: "add-row-top",
              text: "Add Row Above",
              onClick: () => {
                insertRows({ above: true });
              }
            },
            "addRowAbove"
          ),
          /* @__PURE__ */ jsx(
            MenuItem,
            {
              icon: "add-row-top",
              text: "Add Row Below",
              onClick: () => {
                insertRows({});
              }
            },
            "addRowBelow"
          ),
          /* @__PURE__ */ jsx(
            MenuItem,
            {
              icon: "remove",
              text: `Remove Row${selectedRowIds.length > 1 ? "s" : ""}`,
              onClick: () => {
                const selectedRowIds2 = Object.keys(selectedCells2).map(
                  (cellId) => {
                    const [rowId] = cellId.split(":");
                    return rowId;
                  }
                );
                updateEntitiesHelper(entities, (entities2) => {
                  const ents = entities2.filter(
                    (e2, i) => !selectedRowIds2.includes(getIdOrCodeOrIndex(e2, i))
                  );
                  updateValidation(
                    ents,
                    omitBy(
                      reduxFormCellValidation,
                      (v, cellId) => selectedRowIds2.includes(cellId.split(":")[0])
                    )
                  );
                  return ents;
                });
                refocusTable();
              }
            },
            "removeRow"
          )
        ] })
      ] });
      ContextMenu.show(menu, { left: e.clientX, top: e.clientY });
    },
    [
      contextMenu,
      entities,
      handleCopySelectedRows,
      history,
      insertRows,
      isCellEditable,
      isCopyable,
      reduxFormCellValidation,
      refocusTable,
      updateEntitiesHelper,
      updateValidation
    ]
  );
  const getTableRowProps = useCallback(
    (state, rowInfo) => {
      if (!rowInfo) {
        return {
          className: "no-row-data"
        };
      }
      const entity = rowInfo.original;
      const rowId = getIdOrCodeOrIndex(entity, rowInfo.index);
      const rowSelected = reduxFormSelectedEntityIdMap[rowId];
      const isExpanded = expandedEntityIdMap[rowId];
      const rowDisabled = isEntityDisabled(entity);
      const dataId = entity.id || entity.code;
      let noGroupBorder = false;
      if (getCheckboxGroupId) {
        const currentGroupId = getCheckboxGroupId(entity, rowInfo.index);
        const nextEntity = entities[rowInfo.index + 1];
        const nextGroupId = nextEntity ? getCheckboxGroupId(nextEntity, rowInfo.index + 1) : void 0;
        if (currentGroupId && currentGroupId === nextGroupId) {
          noGroupBorder = true;
        }
      }
      return {
        onClick: (e) => {
          if (isCellEditable) return;
          if (e.target.matches(".tg-expander, .tg-expander *")) {
            setExpandedEntityIdMap((prev) => ({ ...prev, [rowId]: !isExpanded }));
            return;
          } else if (e.target.closest(".tg-react-table-checkbox-cell-container")) {
            return;
          } else if (mustClickCheckboxToSelect) {
            return;
          }
          if (e.detail > 1) {
            return;
          }
          rowClick(e, rowInfo, entities, {
            reduxFormSelectedEntityIdMap,
            isSingleSelect,
            noSelect,
            onRowClick,
            isEntityDisabled,
            withCheckboxes,
            onDeselect,
            onSingleRowSelect,
            onMultiRowSelect,
            noDeselectAll,
            onRowSelect,
            change,
            getCheckboxGroupId
          });
        },
        //row right click
        onContextMenu: (e) => {
          e.preventDefault();
          if (rowId === void 0 || rowDisabled || isCellEditable) return;
          const oldIdMap = cloneDeep(reduxFormSelectedEntityIdMap) || {};
          let newIdMap;
          if (withCheckboxes) {
            newIdMap = oldIdMap;
          } else {
            newIdMap = oldIdMap[rowId] ? oldIdMap : { [rowId]: { entity } };
            const keyedEntities = keyBy(entities, getIdOrCodeOrIndex);
            forEach(newIdMap, (val, key) => {
              const freshEntity = keyedEntities[key];
              if (freshEntity) {
                newIdMap[key] = { ...newIdMap[key], entity: freshEntity };
              }
            });
            finalizeSelection({
              idMap: newIdMap,
              entities,
              props: {
                onDeselect,
                onSingleRowSelect,
                onMultiRowSelect,
                noDeselectAll,
                onRowSelect,
                noSelect,
                change
              }
            });
          }
          showContextMenu(e, { idMap: newIdMap, selectedCells });
        },
        className: classNames(
          "with-row-data",
          getRowClassName && getRowClassName(rowInfo, state),
          {
            disabled: rowDisabled,
            selected: rowSelected && !withCheckboxes,
            "rt-tr-last-row": rowInfo.index === entities.length - 1,
            "no-group-border": noGroupBorder
          }
        ),
        "data-test-selected": !!rowSelected,
        "data-test-id": dataId === void 0 ? rowInfo.index : dataId,
        "data-index": rowInfo.index,
        "data-tip": typeof rowDisabled === "string" ? rowDisabled : void 0,
        onDoubleClick: (e) => {
          if (rowDisabled) return;
          onDoubleClick && onDoubleClick(rowInfo.original, rowInfo.index, history, e);
        }
      };
    },
    [
      change,
      entities,
      expandedEntityIdMap,
      getRowClassName,
      history,
      isCellEditable,
      isEntityDisabled,
      isSingleSelect,
      mustClickCheckboxToSelect,
      noDeselectAll,
      noSelect,
      onDeselect,
      onDoubleClick,
      onMultiRowSelect,
      onRowClick,
      onRowSelect,
      onSingleRowSelect,
      reduxFormSelectedEntityIdMap,
      selectedCells,
      showContextMenu,
      withCheckboxes,
      getCheckboxGroupId
    ]
  );
  const getTableCellProps = useCallback(
    (state, rowInfo, column) => {
      if (!isCellEditable) return {};
      if (!rowInfo) return {};
      if (!reduxFormCellValidation) return {};
      const entity = rowInfo.original;
      const rowIndex = rowInfo.index;
      const rowId = getIdOrCodeOrIndex(entity, rowIndex);
      const {
        cellId,
        cellIdAbove,
        cellIdToRight,
        cellIdBelow,
        cellIdToLeft,
        rowDisabled,
        columnIndex
      } = getCellInfo({
        columnIndex: column.index,
        columnPath: column.path,
        rowId,
        schema,
        entities,
        rowIndex,
        isEntityDisabled,
        entity
      });
      const _isClean = entity._isClean && doNotValidateUntouchedRows || isEntityClean(entity);
      const err = !_isClean && reduxFormCellValidation[cellId];
      let selectedTopBorder, selectedRightBorder, selectedBottomBorder, selectedLeftBorder;
      if (selectedCells[cellId]) {
        selectedTopBorder = !selectedCells[cellIdAbove];
        selectedRightBorder = !selectedCells[cellIdToRight];
        selectedBottomBorder = !selectedCells[cellIdBelow];
        selectedLeftBorder = !selectedCells[cellIdToLeft];
      }
      const isPrimarySelected = selectedCells[cellId] === PRIMARY_SELECTED_VAL;
      const className2 = classNames({
        isSelectedCell: selectedCells[cellId],
        isPrimarySelected,
        isSecondarySelected: selectedCells[cellId] === true,
        noSelectedTopBorder: !selectedTopBorder,
        isCleanRow: _isClean,
        noSelectedRightBorder: !selectedRightBorder,
        noSelectedBottomBorder: !selectedBottomBorder,
        noSelectedLeftBorder: !selectedLeftBorder,
        isDropdownCell: column.type === "dropdown",
        isEditingCell: reduxFormEditingCell === cellId,
        hasCellError: !!err,
        "no-data-tip": selectedCells[cellId]
      });
      return {
        onDoubleClick: () => {
          if (rowDisabled) return;
          startCellEdit(cellId);
        },
        ...err && {
          "data-tip": err?.message || err,
          "data-no-child-data-tip": true
        },
        onContextMenu: (e) => {
          const newSelectedCells = { ...selectedCells };
          if (!isPrimarySelected) {
            if (primarySelectedCellId) {
              newSelectedCells[primarySelectedCellId] = true;
            }
            newSelectedCells[cellId] = PRIMARY_SELECTED_VAL;
            setSelectedCells(newSelectedCells);
          }
          showContextMenu(e, { selectedCells: newSelectedCells });
        },
        onClick: (event) => {
          handleCellClick({
            event,
            cellId,
            rowDisabled,
            rowIndex,
            columnIndex
          });
        },
        className: className2
      };
    },
    [
      doNotValidateUntouchedRows,
      entities,
      handleCellClick,
      isCellEditable,
      isEntityDisabled,
      primarySelectedCellId,
      reduxFormCellValidation,
      reduxFormEditingCell,
      schema,
      selectedCells,
      showContextMenu,
      startCellEdit
    ]
  );
  if (withSelectAll && !safeQuery) {
    throw new Error("safeQuery is needed for selecting all table records");
  }
  let compactClassName = "";
  if (compactPaging) {
    compactClassName += " tg-compact-paging";
  }
  compactClassName += extraCompact ? " tg-extra-compact-table" : compact ? " tg-compact-table" : "";
  const hasFilters = filters.length || searchTerm || schema.fields.some(
    (field) => field.filterIsActive && field.filterIsActive(currentParams)
  );
  const additionalFilterKeys = schema.fields.reduce((acc, field) => {
    if (field.filterKey) acc.push(field.filterKey);
    return acc;
  }, []);
  const filtersOnNonDisplayedFields = useMemo(() => {
    const _filtersOnNonDisplayedFields = [];
    if (filters && filters.length) {
      schema.fields.forEach((field) => {
        const ccDisplayName = getCCDisplayName(field);
        if (field.isHidden) {
          filters.forEach((filter) => {
            if (filter.filterOn === ccDisplayName) {
              _filtersOnNonDisplayedFields.push({
                ...filter,
                displayName: field.displayName
              });
            }
          });
        }
      });
    }
    return _filtersOnNonDisplayedFields;
  }, [filters, schema.fields]);
  const numRows = isInfinite ? entities.length : pageSize;
  const idMap = useMemo(
    () => reduxFormSelectedEntityIdMap || {},
    [reduxFormSelectedEntityIdMap]
  );
  const selectedRowCount = Object.keys(idMap).filter((key) => idMap[key]).length;
  let rowsToShow = doNotShowEmptyRows ? Math.min(numRows, entities.length) : numRows;
  if (entities.length === 0 && rowsToShow < 3) rowsToShow = 3;
  const expandedRows = entities.reduce((acc, row, index) => {
    const rowId = getIdOrCodeOrIndex(row, index);
    acc[index] = expandedEntityIdMap[rowId];
    return acc;
  }, {});
  const showHeader = (withTitle || withSearch || children) && !noHeader;
  const toggleFullscreenButton = /* @__PURE__ */ jsx(
    Button,
    {
      icon: "fullscreen",
      active: fullscreen,
      minimal: true,
      onClick: () => setFullscreen((prev) => !prev)
    }
  );
  const { showSelectAll, showClearAll } = useMemo(() => {
    let _showSelectAll = false;
    let _showClearAll = false;
    if (!isInfinite) {
      const canShowSelectAll = withSelectAll || entitiesAcrossPages && numRows < entitiesAcrossPages.length;
      if (canShowSelectAll) {
        let atLeastOneRowOnCurrentPageSelected = false;
        const allRowsOnCurrentPageSelected = entities.every((e) => {
          const rowId = getIdOrCodeOrIndex(e);
          const selected = idMap[rowId] || isEntityDisabled(e);
          if (selected) atLeastOneRowOnCurrentPageSelected = true;
          return selected;
        });
        if (atLeastOneRowOnCurrentPageSelected && allRowsOnCurrentPageSelected) {
          let everyEntitySelected;
          if (isLocalCall) {
            everyEntitySelected = entitiesAcrossPages.every((e) => {
              const rowId = getIdOrCodeOrIndex(e);
              return idMap[rowId] || isEntityDisabled(e);
            });
          } else {
            everyEntitySelected = entityCount <= selectedRowCount;
          }
          if (everyEntitySelected) {
            _showClearAll = selectedRowCount;
          }
          _showSelectAll = !everyEntitySelected;
        }
      }
    }
    return { showSelectAll: _showSelectAll, showClearAll: _showClearAll };
  }, [
    entities,
    entitiesAcrossPages,
    entityCount,
    idMap,
    isEntityDisabled,
    isInfinite,
    isLocalCall,
    numRows,
    selectedRowCount,
    withSelectAll
  ]);
  const showNumSelected = !noSelect && !isSingleSelect && !hideSelectedCount;
  const selectedAndTotalMessage = useMemo(() => {
    let _selectedAndTotalMessage = "";
    if (showNumSelected) {
      _selectedAndTotalMessage += `${selectedRowCount} Selected `;
    }
    if (showCount && showNumSelected) {
      _selectedAndTotalMessage += `/ `;
    }
    if (showCount) {
      if (isEntityCountLoading && entityCount < 1) {
        _selectedAndTotalMessage += `Loading...`;
      } else {
        _selectedAndTotalMessage += `${entityCount || 0} Total`;
      }
    }
    if (_selectedAndTotalMessage) {
      _selectedAndTotalMessage = /* @__PURE__ */ jsx("div", { children: _selectedAndTotalMessage });
    }
    return _selectedAndTotalMessage;
  }, [
    entityCount,
    selectedRowCount,
    showCount,
    showNumSelected,
    isEntityCountLoading
  ]);
  const shouldShowPaging = !isInfinite && withPaging && (hidePageSizeWhenPossible ? entityCount > pageSize : true);
  const SubComponentToUse = useMemo(() => {
    if (SubComponent) {
      return (row) => {
        let shouldShow = true;
        if (shouldShowSubComponent) {
          shouldShow = shouldShowSubComponent(row.original);
        }
        if (shouldShow) {
          return SubComponent(row);
        }
      };
    }
    return;
  }, [SubComponent, shouldShowSubComponent]);
  const nonDisplayedFilterComp = useMemo(() => {
    if (filtersOnNonDisplayedFields.length) {
      const content = filtersOnNonDisplayedFields.map(
        ({ displayName, path, selectedFilter, filterValue }) => {
          let filterValToDisplay = filterValue;
          if (selectedFilter === "inList") {
            filterValToDisplay = Array.isArray(filterValToDisplay) ? filterValToDisplay : filterValToDisplay && filterValToDisplay.split(";");
          }
          if (Array.isArray(filterValToDisplay)) {
            filterValToDisplay = filterValToDisplay.join(", ");
          }
          return /* @__PURE__ */ jsxs(
            "div",
            {
              className: "tg-filter-on-non-displayed-field",
              children: [
                displayName || startCase(camelCase(path)),
                " ",
                lowerCase(selectedFilter),
                " ",
                filterValToDisplay
              ]
            },
            displayName || startCase(camelCase(path))
          );
        }
      );
      return /* @__PURE__ */ jsx("div", { style: { marginRight: 5, marginLeft: "auto" }, children: /* @__PURE__ */ jsx(
        Tooltip,
        {
          content: /* @__PURE__ */ jsxs("div", { children: [
            "Active filters on hidden columns:",
            /* @__PURE__ */ jsx("br", {}),
            /* @__PURE__ */ jsx("br", {}),
            content
          ] }),
          children: /* @__PURE__ */ jsx(Icon, { icon: "filter-list" })
        }
      ) });
    }
    return null;
  }, [filtersOnNonDisplayedFields]);
  const filteredEnts = useMemo(() => {
    if (onlyShowRowsWErrors) {
      const rowToErrorMap = {};
      forEach(reduxFormCellValidation, (err, cellId) => {
        if (err) {
          const [rowId] = cellId.split(":");
          rowToErrorMap[rowId] = true;
        }
      });
      return entities.filter((e) => {
        return rowToErrorMap[e.id];
      });
    }
    return entities;
  }, [entities, onlyShowRowsWErrors, reduxFormCellValidation]);
  const renderColumns = useColumns({
    addFilters,
    cellRenderer,
    columns,
    currentParams,
    compact,
    withDisplayOptions,
    resetDefaultVisibility,
    editingCellSelectAll,
    entities,
    expandedEntityIdMap,
    extraCompact,
    filters,
    formName,
    getCellHoverText,
    isCellEditable,
    isEntityDisabled,
    isLocalCall,
    isSimple,
    isSingleSelect,
    isSelectionARectangle,
    noDeselectAll,
    hideExpandSubCompColumn,
    noSelect,
    noUserSelect,
    onDeselect,
    onMultiRowSelect,
    onRowClick,
    onRowSelect,
    onSingleRowSelect,
    order,
    primarySelectedCellId,
    reduxFormCellValidation,
    reduxFormSelectedEntityIdMap,
    refocusTable,
    removeSingleFilter,
    schema,
    selectedCells,
    setExpandedEntityIdMap,
    setNewParams,
    setOrder,
    setSelectedCells,
    shouldShowSubComponent,
    startCellEdit,
    SubComponent,
    tableRef,
    updateColumnVisibility,
    updateEntitiesHelper,
    updateValidation,
    withCheckboxes,
    withExpandAndCollapseAllButton,
    withFilter,
    withSort,
    recordIdToIsVisibleMap,
    getCheckboxGroupId,
    setRecordIdToIsVisibleMap
  });
  const scrollToTop = useCallback(
    () => tableRef.current?.tableRef?.children?.[0]?.children?.[0]?.scrollIntoView(),
    []
  );
  const reactTable = useMemo(
    () => /* @__PURE__ */ jsx(
      ReactTable,
      {
        data: filteredEnts,
        ref: tableRef,
        noVirtual,
        className: classNames({
          isCellEditable,
          loading: isLoading,
          "tg-table-loading": isLoading,
          "tg-table-disabled": disabled
        }),
        itemSizeEstimator: extraCompact ? itemSizeEstimators.compact : compact ? itemSizeEstimators.normal : itemSizeEstimators.comfortable,
        TfootComponent: () => {
          return /* @__PURE__ */ jsx("button", { children: "hasdfasdf" });
        },
        columns: renderColumns,
        pageSize: rowsToShow,
        expanded: expandedRows,
        showPagination: false,
        sortable: false,
        loading: disabled,
        defaultResized: resized,
        onResizedChange: (newResized = []) => {
          const resizedToUse = newResized.map((column) => {
            if (column.value < 50) {
              return {
                ...column,
                value: 50
              };
            } else {
              return column;
            }
          });
          resizePersist(resizedToUse);
        },
        TheadComponent,
        ThComponent,
        getTrGroupProps: getTableRowProps,
        getTdProps: getTableCellProps,
        NoDataComponent: ({ children: children2 }) => isLoading ? /* @__PURE__ */ jsx("div", { className: "rt-noData", children: "Loading..." }) : /* @__PURE__ */ jsx("div", { className: "rt-noData", children: noRowsFoundMessage || children2 }),
        LoadingComponent: ({ loadingText, loading }) => /* @__PURE__ */ jsx(
          DisabledLoadingComponent,
          {
            loading,
            loadingText,
            disabled
          }
        ),
        style: {
          maxHeight,
          minHeight: 150,
          ...style
        },
        SubComponent: SubComponentToUse,
        ...ReactTableProps
      }
    ),
    [
      ReactTableProps,
      SubComponentToUse,
      TheadComponent,
      compact,
      disabled,
      expandedRows,
      extraCompact,
      filteredEnts,
      getTableCellProps,
      getTableRowProps,
      isCellEditable,
      isLoading,
      maxHeight,
      noRowsFoundMessage,
      renderColumns,
      resizePersist,
      resized,
      rowsToShow,
      style,
      noVirtual
    ]
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      tabIndex: "1",
      style: { height: "100%" },
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp,
      children: /* @__PURE__ */ jsx(
        "div",
        {
          className: classNames(
            "data-table-container",
            extraClasses,
            className,
            compactClassName,
            {
              fullscreen,
              in_cypress_test: window.Cypress,
              //tnr: this is a hack to make cypress be able to correctly click the table without the sticky header getting in the way. remove me once https://github.com/cypress-io/cypress/issues/871 is fixed
              "dt-isViewable": isViewable,
              "dt-minimalStyle": minimalStyle,
              "no-padding": noPadding,
              "hide-column-header": hideColumnHeader
            }
          ),
          children: /* @__PURE__ */ jsxs(
            "div",
            {
              className: "data-table-container-inner",
              ...isCellEditable && {
                tabIndex: -1,
                onKeyDown: (e) => {
                  const isTabKey = e.key === "Tab";
                  const isArrowKey = e.key.startsWith("Arrow");
                  if (isArrowKey && e.target?.tagName !== "INPUT" || isTabKey) {
                    const left = e.key === "ArrowLeft";
                    const up = e.key === "ArrowUp";
                    const down = e.key === "ArrowDown" || e.key === "Enter";
                    let cellIdToUse = primarySelectedCellId;
                    const pathToIndex = getFieldPathToIndex(schema);
                    const entityMap = getEntityIdToEntity(entities);
                    if (!cellIdToUse) return;
                    const {
                      isRect,
                      firstCellIndex,
                      lastCellIndex,
                      lastRowIndex,
                      firstRowIndex
                    } = isSelectionARectangle();
                    if (isRect) {
                      const [rowId3, columnPath2] = cellIdToUse.split(":");
                      const columnIndex2 = pathToIndex[columnPath2];
                      const indexToPath = invert(pathToIndex);
                      if (firstCellIndex === columnIndex2 && firstRowIndex === entityMap[rowId3]?.i) {
                        cellIdToUse = `${entities[lastRowIndex].id}:${indexToPath[lastCellIndex]}`;
                      } else if (firstCellIndex === columnIndex2 && lastRowIndex === entityMap[rowId3]?.i) {
                        cellIdToUse = `${entities[firstRowIndex].id}:${indexToPath[lastCellIndex]}`;
                      } else if (lastCellIndex === columnIndex2 && firstRowIndex === entityMap[rowId3]?.i) {
                        cellIdToUse = `${entities[lastRowIndex].id}:${indexToPath[firstCellIndex]}`;
                      } else {
                        cellIdToUse = `${entities[firstRowIndex].id}:${indexToPath[firstCellIndex]}`;
                      }
                    }
                    if (!cellIdToUse) return;
                    const [rowId2, columnPath] = cellIdToUse.split(":");
                    const columnIndex = pathToIndex[columnPath];
                    const { i: rowIndex } = entityMap[rowId2];
                    const {
                      cellIdAbove,
                      cellIdToRight,
                      cellIdBelow,
                      cellIdToLeft
                    } = getCellInfo({
                      columnIndex,
                      columnPath,
                      rowId: rowId2,
                      schema,
                      entities,
                      rowIndex,
                      isEntityDisabled,
                      entity: entityMap[rowId2].e
                    });
                    const nextCellId = up ? cellIdAbove : down ? cellIdBelow : left ? cellIdToLeft : cellIdToRight;
                    e.stopPropagation();
                    e.preventDefault();
                    if (!nextCellId) return;
                    if (document.activeElement?.parentElement?.classList.contains(
                      "rt-td"
                    )) {
                      document.activeElement.blur();
                    }
                    handleCellClick({
                      event: e,
                      cellId: nextCellId
                    });
                  }
                  if (e.metaKey || e.ctrlKey || e.altKey) return;
                  if (!primarySelectedCellId) return;
                  const entityIdToEntity = getEntityIdToEntity(entities);
                  const [rowId] = primarySelectedCellId.split(":");
                  if (!rowId) return;
                  const entity = entityIdToEntity[rowId].e;
                  if (!entity) return;
                  const rowDisabled = isEntityDisabled(entity);
                  const isNum = e.code?.startsWith("Digit");
                  const isLetter = e.code?.startsWith("Key");
                  const allowedSpecialChars = [
                    "Minus",
                    "Equal",
                    "Backquote",
                    "BracketLeft",
                    "BracketRight",
                    "Backslash",
                    "IntlBackslash",
                    "Semicolon",
                    "Quote",
                    "Comma",
                    "Period",
                    "Slash",
                    "IntlRo",
                    "IntlYen",
                    "Space"
                  ];
                  const isSpecialChar = allowedSpecialChars.includes(e.code);
                  if (!isNum && !isLetter && !isSpecialChar) {
                    return;
                  }
                  if (rowDisabled) return;
                  e.stopPropagation();
                  startCellEdit(primarySelectedCellId, true);
                }
              },
              children: [
                isCellEditable && entities.length > 50 && // test for this!!
                /* @__PURE__ */ jsx(
                  SwitchField,
                  {
                    name: "onlyShowRowsWErrors",
                    inlineLabel: true,
                    label: "Only Show Rows With Errors",
                    onChange: (e) => {
                      setOnlyShowRowsWErrors(e.target.value);
                    }
                  }
                ),
                showHeader && /* @__PURE__ */ jsxs("div", { className: "data-table-header", children: [
                  /* @__PURE__ */ jsxs("div", { className: "data-table-title-and-buttons", children: [
                    tableName && withTitle && /* @__PURE__ */ jsx("span", { className: "data-table-title", children: tableName }),
                    children,
                    topLeftItems
                  ] }),
                  errorParsingUrlString && /* @__PURE__ */ jsx(
                    Callout,
                    {
                      icon: "error",
                      style: {
                        width: "unset"
                      },
                      intent: Intent.WARNING,
                      children: "Error parsing URL"
                    }
                  ),
                  nonDisplayedFilterComp,
                  withSearch && /* @__PURE__ */ jsxs("div", { className: "data-table-search-and-clear-filter-container", children: [
                    leftOfSearchBarItems,
                    hasFilters ? /* @__PURE__ */ jsx(Tooltip, { content: "Clear Filters", children: /* @__PURE__ */ jsx(
                      Button,
                      {
                        minimal: true,
                        intent: "danger",
                        icon: "filter-remove",
                        disabled,
                        className: "data-table-clear-filters",
                        onClick: () => {
                          clearFilters(additionalFilterKeys);
                        }
                      }
                    ) }) : "",
                    /* @__PURE__ */ jsx(
                      SearchBar,
                      {
                        noForm,
                        searchInput: currentParams.searchTerm,
                        setSearchTerm,
                        loading: isLoading,
                        searchMenuButton,
                        disabled,
                        autoFocusSearch
                      }
                    )
                  ] })
                ] }),
                subHeader,
                showSelectAll && !isSingleSelect && /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      marginTop: 5,
                      marginBottom: 5,
                      display: "flex",
                      alignItems: "center"
                    },
                    children: [
                      "All items on this page are selected.",
                      " ",
                      /* @__PURE__ */ jsx(
                        Button,
                        {
                          small: true,
                          minimal: true,
                          intent: "primary",
                          text: `Select all ${entityCount || entitiesAcrossPages.length} items`,
                          loading: selectingAll,
                          onClick: async () => {
                            if (withSelectAll) {
                              setSelectingAll(true);
                              try {
                                const allEntities = await safeQuery(fragment, {
                                  variables: {
                                    where: variables.where,
                                    sort: variables.sort
                                  },
                                  canCancel: true
                                });
                                addEntitiesToSelection(allEntities);
                              } catch (error) {
                                console.error(`error:`, error);
                                window.toastr.error("Error selecting all constructs");
                              }
                              setSelectingAll(false);
                            } else {
                              addEntitiesToSelection(entitiesAcrossPages);
                            }
                          }
                        }
                      )
                    ]
                  }
                ),
                showClearAll > 0 && /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      marginTop: 5,
                      marginBottom: 5,
                      display: "flex",
                      alignItems: "center"
                    },
                    children: [
                      "All ",
                      showClearAll,
                      " items are selected.",
                      /* @__PURE__ */ jsx(
                        Button,
                        {
                          small: true,
                          minimal: true,
                          intent: "primary",
                          text: "Clear Selection",
                          onClick: () => {
                            finalizeSelection({
                              idMap: {},
                              entities,
                              props: {
                                onDeselect,
                                onSingleRowSelect,
                                onMultiRowSelect,
                                noDeselectAll,
                                onRowSelect,
                                noSelect,
                                change
                              }
                            });
                          }
                        }
                      )
                    ]
                  }
                ),
                reactTable,
                isCellEditable && /* @__PURE__ */ jsxs("div", { style: { display: "flex" }, children: [
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        width: "100%",
                        display: "flex",
                        justifyContent: "center"
                      },
                      children: !onlyShowRowsWErrors && /* @__PURE__ */ jsx(
                        Button,
                        {
                          icon: "add",
                          onClick: () => {
                            insertRows({ numRows: 10, appendToBottom: true });
                          },
                          minimal: true,
                          children: "Add 10 Rows"
                        }
                      )
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      onClick: () => {
                        handleCopyTable(tableRef, { isDownload: true });
                      },
                      "data-tip": "Download Table as CSV",
                      minimal: true,
                      icon: "download"
                    }
                  )
                ] }),
                !noFooter && /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: "data-table-footer",
                    style: {
                      justifyContent: !showNumSelected && !showCount ? "flex-end" : "space-between"
                    },
                    children: [
                      selectedAndTotalMessage,
                      /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexWrap: "wrap" }, children: [
                        additionalFooterButtons,
                        !noFullscreenButton && toggleFullscreenButton,
                        withDisplayOptions && /* @__PURE__ */ jsx(
                          DisplayOptions,
                          {
                            doNotSearchHiddenColumns,
                            compact,
                            extraCompact,
                            disabled,
                            hideDisplayOptionsIcon,
                            resetDefaultVisibility,
                            updateColumnVisibility,
                            updateTableDisplayDensity,
                            moveColumnPersist,
                            showForcedHiddenColumns,
                            setShowForcedHidden,
                            hasOptionForForcedHidden: withDisplayOptions && (isSimple || isInfinite),
                            schema
                          }
                        ),
                        shouldShowPaging && /* @__PURE__ */ jsx(
                          PagingTool,
                          {
                            controlled_hasNextPage,
                            controlled_onRefresh,
                            controlled_page,
                            controlled_setPage,
                            controlled_setPageSize,
                            controlled_total,
                            disabled,
                            disableSetPageSize,
                            entities,
                            entityCount,
                            hideSetPageSize,
                            hideTotalPages,
                            keepSelectionOnPageChange,
                            onRefresh,
                            page,
                            pageSize,
                            pagingDisabled,
                            persistPageSize,
                            setPage,
                            setPageSize,
                            setSelectedEntityIdMap: (newIdMap) => {
                              change("reduxFormSelectedEntityIdMap", newIdMap);
                            },
                            scrollToTop
                          }
                        )
                      ] })
                    ]
                  }
                )
              ]
            }
          )
        }
      )
    }
  );
};
const WrappedDT = dataTableEnhancer(DataTable);
export default WrappedDT;
const ConnectedPagingTool = dataTableEnhancer(PagingTool);
export { ConnectedPagingTool };
